import { forwardRef, useEffect, useState } from "react";
import cn from "clsx";
import Feedback from "../feedback";
import { IInputProps } from "../types";

interface IProps extends IInputProps {
    type?: string;
    accept?: string;
    autoComplete?: string;
    maxLength?: number;
    min?: string | number;
    onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
}

const Input = forwardRef<HTMLInputElement, IProps>(
    (
        {
            className,
            type,
            disabled,
            state,
            feedbackText,
            id,
            name,
            onChange,
            onClick,
            onBlur,
            onKeyDown,
            onPaste,
            value,
            min,
            readonly,
            showState,
            showErrorOnly,
            customStyle,
            accept,
            autoComplete,
            maxLength,
            ...restProps
        },
        ref
    ) => {
        const defaultClass =
            "block w-full py-[0.54rem] px-3 text-body leading-relaxed bg-white border border-solid border-ghost placeholder-body";
        const focusClass =
            customStyle !== "nofocus" &&
            !readonly &&
            "focus:shadow-none focus:outline-0 focus:text-body focus:bg-gray-100";
        const readOnlyAndDisabledClass =
            (readonly || disabled) && "bg-gray-300 opacity-100";
        const readOnlyFocusClass =
            customStyle !== "nofocus" &&
            readonly &&
            "focus:shadow-none focus:outline-0  focus:text-body";
        const successClass =
            !showErrorOnly && state === "success" && "!border-success";
        const warningClass =
            !showErrorOnly && state === "warning" && "!border-warning";
        const errorClass = state === "error" && "!border-danger";
        const focusBorderClass = customStyle !== "nofocus" && !state && "";
        const noFocusClass = customStyle === "nofocus" && "focus:outline-0";
        const [cValue, setCValue] = useState<string>(String(value ?? ""))
        useEffect(() => {
            setCValue(String(value ?? ""))
        }, [value])
        return (
            <>
                <input
                    type={type}
                    disabled={disabled}
                    ref={ref}
                    className={cn(
                        "form-control",
                        defaultClass,
                        focusClass,
                        readOnlyAndDisabledClass,
                        readOnlyFocusClass,
                        successClass,
                        warningClass,
                        errorClass,
                        focusBorderClass,
                        noFocusClass,
                        className
                    )}
                    id={id}
                    name={name}
                    min={min}
                    onChange={(e) => {
                        if (onChange) {
                            if (type == "text") {
                                const cleaned = e
                                cleaned.target.value = e.target.value.replaceAll("%", "(percent)")
                                cleaned.target.value = e.target.value.replaceAll("\t", "")
                                onChange(typeof cValue == "string" ? cleaned : e)
                            } else {
                                onChange(e)
                            }
                        }
                        setCValue(e.target.value)
                    }}
                    onClick={onClick}
                    onBlur={onBlur}
                    onKeyDown={onKeyDown}
                    onPaste={onPaste}
                    value={typeof cValue == "string" ? cValue.replaceAll("(percent)", "%") : value}
                    readOnly={readonly}
                    accept={accept}
                    autoComplete={autoComplete}
                    maxLength={maxLength}
                    {...restProps}
                />
                {feedbackText && showState && (
                    <Feedback state={state} showErrorOnly={showErrorOnly}>
                        {feedbackText}
                    </Feedback>
                )}
            </>
        );
    }
);

Input.displayName = "Input";

Input.defaultProps = {
    type: "text",
    showState: true,
    showErrorOnly: true,
};

export default Input;
